import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import PaginatedResponse from "_shared/_api/pagination/responses/paginated-response";
import { getApiEndpointName, api } from "_shared/_stores/api";
import type { AppDispatch } from "_shared/_stores/store-types";
import { logSchemaFailure } from "_shared/_utilities/log-schema-failure";
import Guid from "_shared/guids/guid";
import { HttpStatusCode } from "axios";
import EntityNotificationSummarySchema, {
  EntityNotificationSummaryJSON,
} from "notifications/entity-summaries/_schemas/entity-notification-summary-schema";
import MessageNotificationSchema, {
  MessageNotificationJSON,
} from "notifications/message-notifications/_schemas/message-notification-schema";
import NotificationGroupAPIRequest from "notifications/_api/commands/notification-group-api-request";
import NotificationQuery from "notifications/_api/queries/notification-query";
import Notification from "notifications/notification";
import NotificationSchema, { NotificationJSON } from "notifications/_schemas/notification-schema";
import z from "zod";

type NotificationJson = NotificationJSON | MessageNotificationJSON;

type NotificationIdArgs = {
  notificationId: string;
};

type NotificationIdsArgs = {
  notificationIds: string[];
};

const notificationsListTag = { type: "Notification" as const, id: "LIST" };
// The entity-wide feed is a different audience and a different contract, so it carries its own
// list tag: a personal-inbox change must not force every entity-wide reader to refetch, and an
// entity-wide event must not invalidate somebody's own inbox (issue #4438).
export const entityNotificationsListTag = { type: "Notification" as const, id: "ENTITY_LIST" };
const NotificationJsonZ = z.union([MessageNotificationSchema.MessageNotificationJSONZ, NotificationSchema.NotificationJSONZ]);

function notificationTags(notifications?: NotificationJson[]) {
  return notifications
    ? [...notifications.map((notification) => ({ type: "Notification" as const, id: notification.id })), notificationsListTag]
    : [notificationsListTag];
}

const upsertNotificationApiCaches = (dispatch: AppDispatch, notifications: Notification[]) => {
  dispatch(
    notificationsApi.util.upsertQueryEntries(
      notifications.map((notification) => ({
        endpointName: "getNotificationById" as const,
        arg: { notificationId: notification.id.value },
        value: notification,
      })),
    ),
  );
  dispatch(notificationsApi.util.invalidateTags([notificationsListTag]));
};

export const notificationsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getNotifications: build.query<PaginatedResponse<NotificationJson>, { query: NotificationQuery }>({
      query: ({ query }) => ({ url: "notifications", params: query.searchParams }),
      rawResponseSchema: z.array(NotificationJsonZ),
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: NotificationJson[], meta) =>
        PaginatedResponse.from(response, meta?.response?.headers.get("x-pagination") ?? ""),
      transformErrorResponse: (response, meta) => toNotificationsApiError(response, meta),
      providesTags: (result) => notificationTags(result?.data),
    }),

    getEntityNotifications: build.query<PaginatedResponse<EntityNotificationSummaryJSON>, { query: NotificationQuery }>({
      query: ({ query }) => ({ url: "notifications/entity", params: query.searchParams }),
      rawResponseSchema: z.array(EntityNotificationSummarySchema.EntityNotificationSummaryJSONZ),
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: EntityNotificationSummaryJSON[], meta) =>
        PaginatedResponse.from(response, meta?.response?.headers.get("x-pagination") ?? ""),
      transformErrorResponse: (response, meta) => toNotificationsApiError(response, meta),
      providesTags: [entityNotificationsListTag],
    }),

    getNotificationById: build.query<Notification, NotificationIdArgs>({
      query: ({ notificationId }) => `notifications/${notificationId}`,
      rawResponseSchema: NotificationJsonZ,
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: NotificationJson) => Notification.fromJSON(response),
      transformErrorResponse: (response, meta, { notificationId }) => toNotificationsApiError(response, meta, notificationId),
      providesTags: (_result, _error, { notificationId }) => [{ type: "Notification" as const, id: notificationId }],
    }),

    markNotificationAsSeen: build.mutation<Notification, NotificationIdArgs>({
      query: ({ notificationId }) => ({ url: `notifications/${notificationId}/mark-seen`, method: "POST", body: {} }),
      rawResponseSchema: NotificationJsonZ,
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: NotificationJson) => Notification.fromJSON(response),
      transformErrorResponse: (response, meta, { notificationId }) => toNotificationsApiError(response, meta, notificationId),
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          const { data: notification } = await queryFulfilled;
          upsertNotificationApiCaches(dispatch as AppDispatch, [notification]);
        } catch {
          // Failed requests have no notification data to hydrate into the by-id cache.
        }
      },
    }),

    markNotificationsAsSeen: build.mutation<Notification[], NotificationIdsArgs>({
      query: ({ notificationIds }) => ({ url: "notifications/mark-seen", method: "POST", body: notificationIds }),
      rawResponseSchema: z.array(NotificationJsonZ),
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: NotificationJson[]) => response.map((notification) => Notification.fromJSON(notification)),
      transformErrorResponse: (response, meta) => toNotificationsApiError(response, meta),
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          const { data: notifications } = await queryFulfilled;
          upsertNotificationApiCaches(dispatch as AppDispatch, notifications);
        } catch {
          // Failed requests have no notification data to hydrate into the by-id cache.
        }
      },
    }),

    markAllNotificationsAsSeen: build.mutation<void, void>({
      query: () => ({ url: "notifications/mark-all-seen", method: "POST", body: {} }),
      transformErrorResponse: (response, meta) => toNotificationsApiError(response, meta),
      invalidatesTags: (_result, error) => error ? [] : [notificationsListTag],
    }),

    markNotificationAsUnseen: build.mutation<Notification, NotificationIdArgs>({
      query: ({ notificationId }) => ({ url: `notifications/${notificationId}/mark-unseen`, method: "POST", body: {} }),
      rawResponseSchema: NotificationJsonZ,
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: NotificationJson) => Notification.fromJSON(response),
      transformErrorResponse: (response, meta, { notificationId }) => toNotificationsApiError(response, meta, notificationId),
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          const { data: notification } = await queryFulfilled;
          upsertNotificationApiCaches(dispatch as AppDispatch, [notification]);
        } catch {
          // Failed requests have no notification data to hydrate into the by-id cache.
        }
      },
    }),

    markNotificationsAsUnseen: build.mutation<Notification[], NotificationIdsArgs>({
      query: ({ notificationIds }) => ({ url: "notifications/mark-unseen", method: "POST", body: notificationIds }),
      rawResponseSchema: z.array(NotificationJsonZ),
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: NotificationJson[]) => response.map((notification) => Notification.fromJSON(notification)),
      transformErrorResponse: (response, meta) => toNotificationsApiError(response, meta),
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          const { data: notifications } = await queryFulfilled;
          upsertNotificationApiCaches(dispatch as AppDispatch, notifications);
        } catch {
          // Failed requests have no notification data to hydrate into the by-id cache.
        }
      },
    }),

    markNotificationGroupAsSeen: build.mutation<void, { groupInfo: NotificationGroupAPIRequest }>({
      query: ({ groupInfo }) => ({
        url: "notifications/mark-group-seen",
        method: "POST",
        body: {
          topicCategory: groupInfo.topicCategory,
          topicIds: groupInfo.topicIds,
        },
      }),
      transformErrorResponse: (response, meta) => toNotificationsApiError(response, meta),
      invalidatesTags: (_result, error) => error ? [] : [notificationsListTag],
    }),

    archiveNotification: build.mutation<void, NotificationIdArgs>({
      query: ({ notificationId }) => ({ url: `notifications/${notificationId}/archive`, method: "POST", body: {} }),
      transformErrorResponse: (response, meta, { notificationId }) => toNotificationsApiError(response, meta, notificationId),
      invalidatesTags: (_result, error) => error && !(error instanceof NotificationNotFoundError) ? [] : [notificationsListTag],
    }),

    archiveNotifications: build.mutation<void, NotificationIdsArgs>({
      query: ({ notificationIds }) => ({ url: "notifications/archive", method: "POST", body: notificationIds }),
      transformErrorResponse: (response, meta) => toNotificationsApiError(response, meta),
      invalidatesTags: (_result, error) => error ? [] : [notificationsListTag],
    }),

    unarchiveNotification: build.mutation<void, NotificationIdArgs>({
      query: ({ notificationId }) => ({ url: `notifications/${notificationId}/unarchive`, method: "POST", body: {} }),
      transformErrorResponse: (response, meta, { notificationId }) => toNotificationsApiError(response, meta, notificationId),
      invalidatesTags: (_result, error) => error && !(error instanceof NotificationNotFoundError) ? [] : [notificationsListTag],
    }),
  }),
});

export const {
  useGetNotificationsQuery,
  useGetEntityNotificationsQuery,
  useLazyGetEntityNotificationsQuery,
  useLazyGetNotificationsQuery,
  useGetNotificationByIdQuery,
  useMarkNotificationAsSeenMutation,
  useMarkNotificationsAsSeenMutation,
  useMarkAllNotificationsAsSeenMutation,
  useMarkNotificationAsUnseenMutation,
  useMarkNotificationsAsUnseenMutation,
  useMarkNotificationGroupAsSeenMutation,
  useArchiveNotificationMutation,
  useArchiveNotificationsMutation,
  useUnarchiveNotificationMutation,
} = notificationsApi;

export class NotificationsApiError extends Error {
  constructor(methodName: string | undefined, public readonly error: unknown) {
    const endpointName = methodName ? `.${methodName}` : "";
    super(`Error in notificationsApi${endpointName}`);
  }
}

export class NotificationNotFoundError extends Error {
  public readonly id: Guid;

  constructor(id: Guid, public readonly error?: unknown) {
    super(`Unable to find notification by id: ${id}`);
    this.id = id;
  }
}

export class UserNotificationsNotFoundError extends Error {
  constructor() {
    super("User does not have any notifications");
  }
}

function toNotificationsApiError(response: FetchBaseQueryError, meta?: unknown, notificationId?: string): Error {
  if (response.status === HttpStatusCode.NotFound) {
    if (notificationId) return new NotificationNotFoundError(new Guid(notificationId), response);
    if (getApiEndpointName(meta) === "markAllNotificationsAsSeen" || getApiEndpointName(meta) === "getNotifications") {
      return new UserNotificationsNotFoundError();
    }
  }

  return new NotificationsApiError(getApiEndpointName(meta), response);
}
